<template>
    <div class="xl">
        
        <div class="pb-4">
            <div class="pb-2">
                <span class="font-bold">Receipt Image</span>
                <div class="text-xs">Please upload an image of the receipt below.</div>
            </div>

            <a 
                v-if="refund?.receipt_image"
                :href="'/storage/' + refund.receipt_image" target="_blank">
                <img :src="'/storage/' + refund.receipt_image" class="img-fluid m-1" />
            </a>

            <div v-if="receiptPreview == null" class="flex items-center justify-center w-60">
                <label for="my-file" class="pt-5 pb-2 flex flex-col items-center justify-center w-full h-1/3 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 dark:hover:bg-bray-800 dark:bg-gray-700 hover:bg-gray-100 dark:border-gray-600 dark:hover:border-gray-500 dark:hover:bg-gray-600">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" class="bi bi-camera-fill" viewBox="0 0 16 16">
                        <path d="M10.5 8.5a2.5 2.5 0 1 1-5 0 2.5 2.5 0 0 1 5 0"/>
                        <path d="M2 4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-1.172a2 2 0 0 1-1.414-.586l-.828-.828A2 2 0 0 0 9.172 2H6.828a2 2 0 0 0-1.414.586l-.828.828A2 2 0 0 1 3.172 4zm.5 2a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1m9 2.5a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0"/>
                    </svg>
                    <p class="mb-2 text-sm text-gray-500 dark:text-gray-400"><span class="font-semibold">Click to upload</span></p>
                </label>
                <input type="file" accept="image/*" @change="receiptPreviewImage" class="form-control-file" id="my-file" style="display:none;">
            </div>
            <div><jet-input-error :message="$page.props.errors.receiptImage" class="mt-1" /></div>
            
            <div v-if="receiptPreview" class="p-2 mt-3 relative" style="width: 20%">
                <img :src="receiptPreview" class="img-fluid" />
        
                <div v-if="!showRemoveImage" class="bg-white rounded-md border border-gray p-2 absolute bottom-2 right-2 z-10">
                    <img src="/images/icons/trash.svg" width="20" height="20" alt="DeleteImage"
                        class="cursor-pointer" @click="showRemoveImage = true"
                    />
                </div>

                <!-- Remove Image Div -->
                <div v-show="showRemoveImage" class="z-30 absolute top-8 bg-white text-center rounded-lg border border-gray shadow-lg">
                    <h2 class="bg-red-400 text-white px-4 py-2 rounded-t-lg">Delete Current Image</h2>
                    <div class="my-6">Are you sure?</div>

                    <div class="mt-4 mb-2 w-full">
                        <jet-button @click.prevent="removeCurrentImage" class="mr-3">
                            Yes
                        </jet-button>

                        <jet-tertiary-button @click.prevent="showRemoveImage = false;" class="ml-3">
                            No
                        </jet-tertiary-button>
                    </div>
                </div>
            </div>
        </div>

        <div class="pt-2 pb-1 text-2xl font-bold">Products</div>
        <div class="text-xs pb-2">Please select items and provide details of any issues.</div>

        <jet-input-error v-if="$page.props.errors.itemsToReturn" :message="$page.props.errors.itemsToReturn" class="mb-1" />

        <div class="rounded-t">
            <table class="table w-full">
                <thead>
                    <tr class="h-10 align-bottom bg-gray-light border">
                        <th></th>
                        <th class="w-48"></th>
                        <th class="text-left">Ordered</th>
                        <th class="text-left">Product</th>
                        <th class="text-left">Quantity</th>
                        <th class="text-left w-64">Defect</th>
                        <th class="text-left w-80">Notes</th>
                        <th></th>
                        <th class="text-left w-48">Refund Image</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(item,index) in ordered_products" :key="index">
                        <td class="text-left">
                            <input type="checkbox" v-model="form.itemsToReturn[item.id]" :value="item.id" class="h-5 w-5 rounded" />
                        </td>
                        <td class="text-left">
                            <img v-if="item.image != null" :src="'/storage/' + item.image" class="mx-auto max-h-20" alt="Product Image" loading="lazy" />
                            <img v-show="item.image == null" src="/images/product-placeholder.png" class="mx-auto max-h-20" alt="No Image" loading="lazy" />
                        </td>
                        <td class="text-left">
                            {{ item.quantity }}
                        </td>
                        <td class="text-left">
                            {{ item.title }}
                            <span v-if="item.description"> - {{ item.description }}</span>
                            <span v-if="item.size"> - {{ item.size }}</span>
                            <span v-if="item.product.special_order" class="ml-1 text-xs italic text-red">(Special Order Item)</span>
                        </td>
                        <td class="text-left">
                            <jet-input-quantity
                                v-if="form.itemsToReturn[item.id]"
                                v-model.number="form.qtyToAdd[item.id]"
                                class="quantity text-center h-8"
                                placeholder="Qty"
                                :step="0.25"
                                :max="item.quantity"
                                :ref="'qtyToAdd'"
                                @keydown.enter.prevent
                                :required="form.itemsToReturn[item.id]"
                            />
                            <jet-input-error v-if="form.itemsToReturn[item.id]" :message="$page.props.errors['qtyToAdd.' + item.id]" class="mt-1" />
                        </td>
                        <td class="md:table-cell text-left">
                            <multiselect 
                                v-if="form.itemsToReturn[item.id]"
                                class="multi-lg h-8 text-base"
                                v-model="form.itemReason[item.id]"
                                :options="returnReasons"
                                valueProp="value"
                                trackBy="label"
                                label="label"
                                placeholder="Select Defect"
                                ref="itemReason"
                                :required="form.itemsToReturn[item.id]"
                            >
                            </multiselect>
                            <jet-input-error v-if="form.itemsToReturn[item.id]" :message="$page.props.errors['itemReason.' + item.id]" class="mt-1" />
                        </td>
                        <td class="md:table-cell text-left">
                            <div v-if="item.parent_id">
                                Part of {{ item.parent_item?.title }}
                            </div>
                            <textarea 
                                v-if="form.itemsToReturn[item.id]"
                                v-model="form.returnNote[item.id]"
                                class="w-full" 
                                rows="2" 
                                placeholder="Type A Description..."
                                ref="returnNote"
                                :required="form.itemsToReturn[item.id] && !isAdmin"
                            >
                            </textarea>
                            <jet-input-error v-if="form.itemsToReturn[item.id]" :message="$page.props.errors['returnNote.' + item.id]" class="mt-1" />
                        </td>
                        <td>
                            <jet-button 
                                type="button"
                                v-if="item.parent_id"
                                @click="creditComboItems(item.id)"    
                            >
                                Credit all items in the combo
                            </jet-button>
                        </td>
                        <td class="text-left">
                            <a v-if="item.refund_order_item?.item_image" :href="'/storage/' + item.refund_order_item.item_image" 
                                target="_blank"
                            >
                                <img :src="'/storage/' + item.refund_order_item.item_image" class="mx-auto max-h-20 border border-transparent rounded-md mb-1" alt="Product Image" loading="lazy" />
                            </a>

                            <image-upload
                                v-if="form.itemsToReturn[item.id] && !noImageReturnReasons.includes(form.itemReason[item.id])"
                                :form="form"
                                :item="item"
                                :itemPreview="itemPreview"
                            >
                            </image-upload>
                            <jet-input-error v-if="form.itemsToReturn[item.id]" :message="$page.props.errors['itemImages.' + item.id]" class="mt-1" />
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>
</template>

<script>
import { usePage } from '@inertiajs/inertia-vue3';
import UseUserAuth from '@/Composables/UseUserAuth';
import Multiselect from '@vueform/multiselect';
import JetLabel from '@/Jetstream/Label';
import JetDangerButton from '@/Jetstream/DangerButton';
import JetInput from '@/Jetstream/Input';
import JetInputError from '@/Jetstream/InputError';
import JetSecondaryButton from '@/Jetstream/SecondaryButton';
import JetTertiaryButton from '@/Jetstream/TertiaryButton';
import JetButton from '@/Jetstream/Button';
import JetInputQuantity from '@/Jetstream/InputQuantity';
import ImageUpload from '@/Pages/Refund/Partials/ImageUpload.vue';
import Compressor from 'compressorjs';

import '@vueform/multiselect/themes/default.css';

export default {
    setup () {
        const { isAdmin, isCustomer } = UseUserAuth(usePage().props.value.user);

        return { isAdmin, isCustomer }
    },

    components: {
        JetDangerButton,
        JetInput,
        JetInputError,
        JetLabel,
        JetSecondaryButton,
        JetTertiaryButton,
        Multiselect,
        JetButton,
        JetInputQuantity,
        ImageUpload,
    },

    props: [
        'form',
        'ordered_products',
        'noImageReturnReasons',
        'returnReasons',
        'refund'
    ],

    data() {
        var itemPreview = {};

        return {
            receiptPreview: null,
            showRemoveImage: false,
            itemPreview: itemPreview,
        }
    },

    methods: {
        removeCurrentImage () {
            this.receiptPreview = null;
            this.form.receiptImage = null;
            this.showRemoveImage = false;
        },

        receiptPreviewImage(event) {
            var inputReceipt = event.target;

            if (inputReceipt.files) {
                var reader = new FileReader();
                reader.onload = (e) => {
                    this.receiptPreview = e.target.result;
                }

                let file = inputReceipt.files[0];
                new Compressor(file, {
                    quality: .8,
                    maxWidth: 800,
                    resize: true,

                    success: (result) => {
                        let newFile = new File([result], file.name, { type: file.type, lastModified: new Date().getTime() });

                        this.form.receiptImage = newFile;
                    },

                    error: (error) => {
                        console.log(error);
                    },
                });

                reader.readAsDataURL(inputReceipt.files[0]);
            }
        },

        creditComboItems(orderItemId) {
            let selectedOrderItem = this.ordered_products.find(op=>op.id === orderItemId);
            if (!selectedOrderItem) return;

            for (let op of this.ordered_products){
                if(op.parent_id === selectedOrderItem.parent_id){
                    this.form.itemsToReturn[op.id] = true;
                    this.form.qtyToAdd[op.id] = this.form.qtyToAdd[orderItemId] ?? 0;
                    this.form.itemReason[op.id] = this.form.itemReason[orderItemId] ?? null;
                }
            }
        }
    },
}

</script>